import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { RubricCriterionRow } from './RubricCriterionRow';

function parseRubricLines(rubric: string): string[] {
  return rubric
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

type RubricCardProps = {
  rubricText?: string;
  onCriterionSelect?: (line: string) => void;
  className?: string;
};

export function RubricCard({
  rubricText,
  onCriterionSelect,
  className,
}: RubricCardProps): ReactElement {
  const { t } = useTranslation();
  const lines = useMemo(() => parseRubricLines(rubricText || ''), [rubricText]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  return (
    <div
      className={cn(
        'rounded-dg-card border border-slate-200/80 bg-white p-4 shadow-sm dark:border-slate-700/70 dark:bg-slate-900/50',
        className
      )}
    >
      <h3 className="text-dg-card-title font-semibold text-slate-900 dark:text-white">
        {t('audit.gradeStudio.rubricTitle')}
      </h3>
      {lines.length === 0 ? (
        <p className="mt-3 text-dg-body text-slate-600 dark:text-slate-400">
          {t('audit.gradeStudio.rubricEmpty')}
        </p>
      ) : (
        <ul className="mt-3 flex max-h-56 flex-col gap-2 overflow-y-auto custom-scrollbar">
          {lines.map((line, i) => (
            <li key={`${i}-${line.slice(0, 24)}`}>
              <RubricCriterionRow
                title={line}
                selected={selectedIndex === i}
                onSelect={
                  onCriterionSelect
                    ? () => {
                        setSelectedIndex(i);
                        onCriterionSelect(line);
                      }
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
